import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { SeverityDistribution } from "../../../utils/hooks/dashboard/useExecutiveSummaryData";
import { getNewSeverityDistribution } from "../../../utils/api/endpoints/dashboard/dashboardApi";
import { FiLoader } from "react-icons/fi";
import { MdInfo } from "react-icons/md";
import ChartFilter, { getPeriodLabel, TimeRange } from "./ChartFilter";

interface SeverityDistributionChartProps {
  data: SeverityDistribution;
  isSubscriptionOnly?: boolean;
  selectedYear?: number | null;
  subscriptionStartDate?: string | null;
}

const SEVERITY_COLORS: Record<string, string> = {
  critical: "#EF4444", // Red-500
  high: "#F97316", // Orange-500
  medium: "#FACC15", // Yellow-400
  low: "#22C55E", // Green-500
  informational: "#64748B", // Slate-500
};

const SeverityDistributionChart: React.FC<SeverityDistributionChartProps> = ({ 
  data: initialData, 
  isSubscriptionOnly = false, 
  selectedYear = null,
  subscriptionStartDate 
}) => {
  const [timeFilter, setTimeFilter] = useState<TimeRange>(
    (selectedYear !== null || isSubscriptionOnly) ? 'yearly' : 'all'
  );
  const [localData, setLocalData] = useState<SeverityDistribution>(initialData);
  const [periodLabel, setPeriodLabel] = useState<string>(initialData?.periodInfo?.label || 'Yearly');
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialMount, setIsInitialMount] = useState(true);
  const [showInfo, setShowInfo] = useState(false);

  // Sync when parent data changes
  useEffect(() => {
    setLocalData(initialData);
    if (initialData?.periodInfo?.label) {
      setPeriodLabel(initialData.periodInfo.label);
    }
  }, [initialData]);

  // Sync local timeFilter with global filters when they change
  useEffect(() => {
    if (selectedYear !== null || isSubscriptionOnly) {
      setTimeFilter('yearly');
    } else {
      setTimeFilter('all');
    }
  }, [selectedYear, isSubscriptionOnly]);

  const currentYear = new Date().getFullYear();
  const effectiveTimeFilter = (selectedYear && selectedYear !== currentYear) 
    ? 'yearly' 
    : timeFilter;

  // Fetch filtered data when timeFilter, isSubscriptionOnly or selectedYear changes
  useEffect(() => {
    if (isInitialMount) {
      setIsInitialMount(false);
      return;
    }

    let isMounted = true;
    const fetchFilteredData = async () => {
      setIsLoading(true);
      try {
        const response = await getNewSeverityDistribution(effectiveTimeFilter, isSubscriptionOnly, selectedYear);
        if (isMounted && response?.success && response.data) {
          setLocalData(response.data);
          if (response.data.periodInfo?.label) {
            setPeriodLabel(response.data.periodInfo.label);
          }
        }
      } catch (e) {
        console.error("Failed to fetch filtered severity distribution", e);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchFilteredData();
    return () => { isMounted = false; };
  }, [effectiveTimeFilter, isSubscriptionOnly, selectedYear]);

  const displayFilterName = periodLabel;

  const chartData = [
    { name: "Critical", value: localData.critical, key: "critical" },
    { name: "High", value: localData.high, key: "high" },
    { name: "Medium", value: localData.medium, key: "medium" },
    { name: "Low", value: localData.low, key: "low" },
    { name: "Informational", value: localData.informational, key: "informational" },
  ].filter(item => item.value > 0);

  return (
    <motion.div
      className="bg-white rounded-xl shadow-sm border border-slate-100 flex flex-col relative"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      <div className="px-6 py-4 border-b border-gray-50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex flex-col min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-slate-800 break-words min-w-0">
            Severity Distribution
          </h3>
          <span className="text-[11px] text-slate-400 font-semibold mt-0.5">
            {(selectedYear && selectedYear !== currentYear) ? `Year ${selectedYear}` : getPeriodLabel(timeFilter, isSubscriptionOnly, subscriptionStartDate)}
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          <div className="relative flex-shrink-0">
            <MdInfo 
              className="h-4 w-4 text-slate-300 hover:text-blue-500 cursor-help transition-colors"
              onMouseEnter={() => setShowInfo(true)}
              onMouseLeave={() => setShowInfo(false)}
            />
            <AnimatePresence>
              {showInfo && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  className="absolute left-0 top-full mt-2 z-50 w-64 bg-[#010D2C] text-white p-3 rounded-xl text-[11px] font-medium shadow-xl pointer-events-none leading-relaxed"
                >
                  Breakdown of all discovered vulnerabilities categorized by their potential impact and risk level (includes accepts risk/non-actionable reports).
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          {(!selectedYear || selectedYear === currentYear) && (
            <ChartFilter
              active={timeFilter}
              onChange={setTimeFilter}
              isSubscriptionOnly={isSubscriptionOnly}
              subscriptionStartDate={subscriptionStartDate}
              supportsAllTime={!selectedYear}
              selectedYear={selectedYear}
            />
          )}
        </div>
      </div>

      <div className="pt-6 px-6 pb-3 flex-1 flex flex-col items-center justify-center relative">
        <AnimatePresence>
          {isLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 backdrop-blur-sm rounded-b-xl"
            >
              <FiLoader className="w-8 h-8 text-blue-500 animate-spin" />
            </motion.div>
          )}
        </AnimatePresence>

        <div className="h-[300px] w-full relative">
          <ResponsiveContainer width="100%" height="100%" debounce={50}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={70}
                outerRadius={105}
                paddingAngle={4}
                dataKey="value"
                animationDuration={400}
              >
                {chartData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={SEVERITY_COLORS[entry.key]} 
                    stroke="none"
                    className="hover:opacity-80 transition-opacity cursor-pointer focus:outline-none"
                  />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  borderRadius: '16px', 
                  border: 'none', 
                  boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                  fontSize: '11px',
                  fontWeight: '800'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
          
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-3xl font-black text-slate-800 leading-none">{localData.total}</span>
            <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest mt-1">Found Issues</span>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-3 sm:grid-cols-5 gap-y-4 gap-x-2 w-full max-w-lg mx-auto">
          {Object.entries(SEVERITY_COLORS).map(([key, color]) => {
            const label = key.charAt(0).toUpperCase() + key.slice(1);
            return (
              <div key={key} className="flex items-center gap-2 justify-center">
                <div 
                  className="h-3 w-3 rounded-[3px]" 
                  style={{ backgroundColor: color }} 
                />
                <span className="text-[10px] font-bold text-slate-500">{label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
};

export default React.memo(SeverityDistributionChart);

